// src/components/reviews/components/ReviewReplyModal.jsx
import React, { useState, useEffect } from 'react';
import { Star, User, X } from 'lucide-react';

// shadcn components
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Textarea } from '../../ui/textarea';
import { Alert, AlertDescription } from '../../ui/alert';
import { Spinner } from '../../ui/spinner';

/**
 * Review Reply Modal - Modal for replying to reviews
 */
const ReviewReplyModal = ({ isOpen, onClose, review, onSubmit, isSubmitting }) => {
  const [replyText, setReplyText] = useState('');
  const [charCount, setCharCount] = useState(0);
  const maxChars = 4096; // Google's limit

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setReplyText('');
      setCharCount(0);
    }
  }, [isOpen]);

  // Update character count
  useEffect(() => {
    setCharCount(replyText.length);
  }, [replyText]);

  // Handle submit
  const handleSubmit = () => {
    if (replyText.trim() && replyText.length <= maxChars) {
      onSubmit(replyText.trim());
    }
  };

  // Render star rating
  const renderStars = (rating) => {
    return [...Array(5)].map((_, i) => (
      <Star
        key={i}
        className={`h-4 w-4 ${
          i < rating
            ? 'text-yellow-500 fill-yellow-500'
            : 'text-gray-300'
        }`}
      />
    ));
  };

  if (!review) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Reply to Review</DialogTitle>
          <DialogDescription>
            Write a professional response to this customer's review
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Original Review */}
          <div className="bg-gray-50 p-4 rounded-lg space-y-3">
            <div className="flex items-start gap-3">
              {/* Avatar */}
              <div className="bg-gradient-to-br from-blue-500 to-purple-600 text-white font-semibold w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0">
                {review.reviewer?.profilePhotoUrl ? (
                  <img
                    src={review.reviewer.profilePhotoUrl}
                    alt={review.reviewer.displayName}
                    className="w-10 h-10 rounded-full"
                  />
                ) : (
                  <User className="h-5 w-5" />
                )}
              </div>

              {/* Name & Rating */}
              <div className="flex-1">
                <p className="font-medium">
                  {review.reviewer?.displayName || 'Anonymous'}
                </p>
                <div className="flex items-center gap-1 mt-1">
                  {renderStars(review.starRating || 0)}
                </div>
              </div>
            </div>

            {/* Review Text */}
            {review.comment && (
              <p className="text-gray-700 pl-13">{review.comment}</p>
            )}
          </div>

          {/* Reply Textarea */}
          <div className="space-y-2">
            <Textarea
              placeholder="Write your response..."
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              rows={6}
              className="resize-none"
              disabled={isSubmitting}
            />
            <div className="flex items-center justify-between text-sm">
              <p className="text-gray-600">
                Tips: Be professional, thank the customer, and address their feedback
              </p>
              <p className={`${charCount > maxChars ? 'text-red-600' : 'text-gray-600'}`}>
                {charCount} / {maxChars}
              </p>
            </div>
          </div>

          {/* Error/Warning */}
          {charCount > maxChars && (
            <Alert variant="destructive">
              <AlertDescription>
                Reply exceeds maximum character limit of {maxChars}
              </AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!replyText.trim() || charCount > maxChars || isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Spinner className="size-4 mr-2" />
                Posting...
              </>
            ) : (
              'Post Reply'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ReviewReplyModal;
